import React from 'react';

import LinkWithPrevLocation from '@common/react/components/UI/LinkWithPrevLocation/LinkWithPrevLocation';
import Avatar from '@common/react/components/Forms/Avatar';
import Transmutation from '@common/react/components/UI/Transmutation/Transmutation';
import TransmutationBack from '@common/react/components/UI/Transmutation/TransmutationBack';
import {
	SearchObjectType,
	SearchObject,
	CustomItemDelegate,
} from '@common/react/components/UI/SearchModal/index';
import { Item } from '@common/react/components/UI/Menu/Menu';

interface SearchModalItemProps<T extends number> {
	item: SearchObject<T>;
	search: string;
	onClick: (e, item: SearchObject<T>) => void;
	isSelected?: boolean;
	onSelect?: (item: SearchObject<T>) => void;
	allowTransmutation?: boolean;
	userLoadingId: number;
	currentUserId: number;
	getPathFromType: (type: T) => string;
	additional?: (item: SearchObject<T>) => React.ReactNode;
	customItem?: CustomItemDelegate<T>;
	onTransmutation?: () => void;
}

const SearchModalItem: <T extends number>(
	p: SearchModalItemProps<T>
) => React.ReactElement<T> = <T extends number>(
	props: SearchModalItemProps<T>,
) => {
	const {
		item,
		isSelected,
		onSelect,
		search,
		allowTransmutation,
		userLoadingId,
		currentUserId,
		getPathFromType,
		onClick,
		customItem = ({
			item,
			menuItem,
		}) => <Item
			key={`${item.firstName}${item.path}`}
			item={{
				...menuItem,
				className: `${menuItem.className ?? ''} ${isSelected ? 'search-modal-item__selected' : ''}`,
			}}
			onClick={(e) => onClick(e, item)}
			withChildren={!!menuItem.children}
			basePath={menuItem?.basePath || ''}
		/>,
		additional,
		onTransmutation,
	} = props;
	const ref = React.useRef<HTMLLIElement>(null);
	React.useEffect(() => {
		if (isSelected && item?.scrollIntoView) {
			item.scrollIntoView = undefined;
			ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [isSelected]);

	if (item.menuItem) {
		return <>
			{customItem({
				item,
				menuItem: item.menuItem,
				search,
				isSelected,
				onSelect,
				ref,
				onClick,
			})}
		</>;
	}

	if (item.type === undefined) return <></>;

	return <li
		ref={ref}
		className={`search-modal-item ${isSelected ? 'search-modal-item__selected' : ''}`}
		onMouseMove={() => !isSelected && onSelect && onSelect(item)}
	>
		<LinkWithPrevLocation
			style={{ backgroundColor: item.color }}
			to={`/${getPathFromType(item.type)}-editor/${item.id}`}
			onClick={(e) => onClick(e, item)}
		>
			<span>
				{item.transmutation || item.type === SearchObjectType.Menu ? null : <div className="search-modal-item__avatar">
					<Avatar
						type=""
						object={item}
						readonly
						className="avatar-container"
						showOpenIcon={false}
						showDownload={false}
						withModal={false}
						firstName={item.firstName}
						lastName={item.lastName}
					/>
				</div>}
				{item.firstName}
				{' '}
				{item.lastName}
			</span>
			{item.transmutation ? <TransmutationBack className="search-modal-item__transmutation" />
				: item.type === SearchObjectType.User && allowTransmutation && currentUserId !== item.id ? (
					<div
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}
						className={userLoadingId === item.id ? 'isLoading loading-component' : ''}
					>
						<span>
							<Transmutation
								onSuccess={onTransmutation}
								requestProps={{ id: item.id }}
								title="Login as (Ctrl + B)"
								className="search-modal-item__transmutation"
							/>
						</span>
					</div>
				) : null}
			{isSelected && item.type !== SearchObjectType.User ? <i className="fa fa-level-down" /> : null}
		</LinkWithPrevLocation>
		{additional?.(item)}
	</li>;
};

export default SearchModalItem;
